/* eslint-disable import/no-named-as-default */
/* eslint-disable camelcase */
/* eslint-disable no-shadow */

import { APIClient } from '@src/client';

/**
 * ISO-8601 day names
 */
export type DayOfWeek = 'MON' | 'TUE' | 'WED' | 'THU' | 'FRI' | 'SAT' | 'SUN';

/**
 * Time window for allowed follow-up messages
 */
export type AllowedWindow = {
  days: DayOfWeek[];
  start: string;
  end: string;
};

/**
 * Follow-up step configuration
 */
export type FollowUpStep = {
  n: number;
  delayMinutes: number;
};

/**
 * Assistant follow-up configuration
 */
export type AssistantFollowUpConfig = {
  enabled: boolean;
  timezone: string;
  steps: FollowUpStep[];
  allowedWindows: AllowedWindow[];
};

// eslint-disable-next-line no-shadow
export const enum AssistantApiErrorCode {
  RESOURCE_ALREADY_EXISTS = 'RESOURCE_ALREADY_EXISTS',
}

export const MCP_OAUTH_CALLBACK_MESSAGE_TYPE = 'MCP_OAUTH_CALLBACK';
export const MCP_OAUTH_BROADCAST_CHANNEL = 'mcp_oauth';

// eslint-disable-next-line no-shadow
export enum MCPConnectionStatus {
  DRAFT = 'DRAFT',
  ACTIVE = 'ACTIVE',
  DISABLED = 'DISABLED',
}

export type MCPConnection = {
  id: string;
  name: string;
  baseUrl: string;
  status: MCPConnectionStatus;
  enabledToolsCount: number;
  enabledTools: string[];
  updatedAt: string;
};

// eslint-disable-next-line no-shadow
export const enum MCPAuthType {
  NONE = 'NONE',
  ACCESS_TOKEN = 'ACCESS_TOKEN',
  CUSTOM_HEADERS = 'CUSTOM_HEADERS',
  OAUTH2 = 'OAUTH2',
}

type MCPAuthNone = {
  type: MCPAuthType.NONE;
};

type MCPAuthAccessToken = {
  type: MCPAuthType.ACCESS_TOKEN;
  accessToken: string;
};

type MCPAuthCustomHeaders = {
  type: MCPAuthType.CUSTOM_HEADERS;
  headers: Record<string, string>;
};

type MCPAuthOAuth2 = {
  type: MCPAuthType.OAUTH2;
  clientId: string;
  clientSecret: string;
  refreshToken: string;
};

export type MCPConnectionAuth =
  | MCPAuthNone
  | MCPAuthAccessToken
  | MCPAuthCustomHeaders
  | MCPAuthOAuth2;

export type CreateMcpConnectionRequest = {
  name: string;
  baseUrl: string;
  description: string;
  auth: MCPConnectionAuth;
};

export type McpOAuthAuthorizeRequest = {
  clientId: string;
  clientSecret: string;
  serverUrl: string;
  redirectUri: string;
};

export type McpOAuthAuthorizeResponse = {
  id: string;
  authorizationUrl: string;
};

export type McpOAuthTokenRequest = {
  state: string;
  code: string;
};

export type McpOAuthTokenResponse = {
  refreshToken: string;
};

export type CreateMcpConnectionResponse = {
  id: string;
  status: MCPConnectionReadyStatus;
};

// eslint-disable-next-line no-shadow
export const enum MCPDiscoveryStatus {
  CONNECTING = 'CONNECTING',
  SUCCEEDED = 'SUCCEEDED',
  FAILED = 'FAILED',
}

export type DiscoverMcpConnectionResponse = {
  operationId: string;
  status: MCPDiscoveryStatus;
};

// eslint-disable-next-line no-shadow
export const enum MCPConnectionReadyStatus {
  READY = 'READY',
  ERROR = 'ERROR',
}

export type MCPOperationError = {
  code: string;
  message: string;
};

export type GetMcpOperationResponse = {
  operationId: string;
  status: MCPDiscoveryStatus;
  connectionStatus: MCPConnectionReadyStatus;
  error?: MCPOperationError;
};

export type MCPTool = {
  toolName: string;
  description: string;
  enabled: boolean;
};

export type GetMcpConnectionResponse = {
  id: string;
  name: string;
  baseUrl: string;
  description: string;
  authType: MCPAuthType;
  status: MCPConnectionReadyStatus;
  tools: MCPTool[];
};

export type UpdateMcpConnectionToolsRequest = {
  enabledToolNames: string[];
};

export type UpdateMcpConnectionToolsResponse = {
  status: MCPConnectionReadyStatus;
  enabledToolsCount: number;
};

export type UpdateMcpConnectionDetailsRequest = {
  name: string;
  description: string;
};

export type UpdateMcpConnectionDetailsResponse = {
  id: string;
  name: string;
  description: string;
  status: MCPConnectionStatus;
};

export type AssistantTable = {
  id: string;
  avatarUrl: string;
  name: string;
  targetType: string; //"GENERAL",
  status: string; //"ACTIVE",
  description: string;
  channels: number[];
  createdAt: string;
  temperature: number;
  businessProcess: number;
  vectorStoreId: string;
  enabledModes: AssistantEnabledMode[];
  enabledCapabilities: AssistantCapabilities[];
};

export const enum AssistantCapabilities {
  REDIRECTION = 'REDIRECTION',
  FOLLOWUP = 'FOLLOWUP',
}

export type AssistantTableFilters = {
  onlyActive?: boolean; // default false
  nameLike?: string; //minimun 3 characters
};

export enum AssistantStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  DRAFTED = 'DRAFTED',
  TESTING = 'TESTING',
}

export enum AssistantModel {
  GPT_4_0 = 'GPT_4_0',
  GPT_4_0_MINI = 'GPT_4_0_MINI',
  GPT_4_1 = 'GPT_4_1',
  GPT_4_1_MINI = 'GPT_4_1_MINI',
  GPT_4_1_NANO = 'GPT_4_1_NANO',
  GPT_5_0 = 'GPT_5_0',
  GPT_5_0_MINI = 'GPT_5_0_MINI',
  GPT_5_0_NANO = 'GPT_5_0_NANO',
  GPT_5_1 = 'GPT_5_1',
  GPT_5_2 = 'GPT_5_2',
}

export const AssistantModelLabel = {
  [AssistantModel.GPT_4_0]: 'GPT-4.0',
  [AssistantModel.GPT_4_0_MINI]: 'GPT-4.0 mini',
  [AssistantModel.GPT_4_1]: 'GPT-4.1',
  [AssistantModel.GPT_4_1_MINI]: 'GPT-4.1 mini',
  [AssistantModel.GPT_4_1_NANO]: 'GPT-4.1 nano',
  [AssistantModel.GPT_5_0]: 'GPT-5.0',
  [AssistantModel.GPT_5_0_MINI]: 'GPT-5.0 mini',
  [AssistantModel.GPT_5_0_NANO]: 'GPT-5.0 nano',
  [AssistantModel.GPT_5_1]: 'GPT-5.1',
  [AssistantModel.GPT_5_2]: 'GPT-5.2',
} as Record<string, string>;

export enum AssistantTarget {
  GENERAL = 'GENERAL',
  DEPARTMENT = 'DEPARTMENT',
}

export enum AssistantRedirects {
  SMART_REDIRECTION = 'SMART_REDIRECTION',
  NO_REDIRECTION = 'NO_REDIRECTION',
  ON_DEMAND = 'ON_DEMAND',
}

export type AssistantForm = {
  id?: string;
  avatarUrl?: string;
  name: string;
  description: string;
  status: AssistantStatus;
  instructions: string;
  model: AssistantModel;
  temperature: number;
  verbosity: string;
  effort: string;
  targetType: AssistantTarget;
  businessProcess: number;
  channels: number[];
  redirection?: AssistantRedirection[];
  testingAccountId?: string[];
  enabledModes?: AssistantEnabledMode[];
  followUp?: AssistantFollowUpConfig;
  /**
   * Messages the agent sends when a contact shares content it cannot read,
   * one per content type. `undefined` = the agent uses that type's system
   * default (sent as `null`); a string = the agent's own text. Mapped to the
   * API's three flat fields in `assistantApi` (see
   * `@src/utils/assistant/unsupportedMessages`).
   */
  unsupportedFileMessages?: UnsupportedFileMessages;
};

// eslint-disable-next-line no-shadow
export enum UnsupportedContentType {
  DOCUMENTS = 'documents',
  VIDEOS = 'videos',
  OTHERS = 'others',
}

export type UnsupportedFileMessages = Partial<
  Record<UnsupportedContentType, string>
>;

/** The three per-agent replies as the assistant API names them (F001, D1). */
export type UnsupportedMessageApiField =
  | 'unsupportedDocumentMessage'
  | 'unsupportedVideoMessage'
  | 'unsupportedOtherContentMessage';

/** Request side: `null` = "use the system default" (create and update alike). */
export type UnsupportedMessageApiFields = Record<
  UnsupportedMessageApiField,
  string | null
>;

/** Read-only companion of the replies: `true` = the effective text is the system default. */
export type UnsupportedMessagesDefaultsFlags = {
  document: boolean;
  video: boolean;
  otherContent: boolean;
};

/** `GET /assistants/{id}` — effective texts + flags (optional: older backends omit them). */
export type AssistantApiRecord = Omit<
  AssistantForm,
  'unsupportedFileMessages' | 'followUp'
> &
  Partial<UnsupportedMessageApiFields> & {
    unsupportedMessagesDefaults?: UnsupportedMessagesDefaultsFlags;
  };

/** `POST /assistants` / `PUT /assistants/{id}` body. */
export type AssistantApiBody = Omit<
  AssistantForm,
  'unsupportedFileMessages' | 'followUp'
> &
  UnsupportedMessageApiFields;

/** `GET /assistants/message-defaults` response. */
export type AssistantMessageDefaultsResponse = Record<
  UnsupportedMessageApiField,
  string
>;

/** The system default texts keyed by content type (what the UI consumes). */
export type AssistantMessageDefaults = Record<UnsupportedContentType, string>;

export enum AssistantEnabledMode {
  TEXT = 'TEXT',
  IMAGE = 'IMAGE',
  AUDIO = 'AUDIO',
  MCP = 'MCP',
}

export type AssistantRedirection = {
  type: AssistantRedirects;
  description?: string;
  redirectionAttempts?: number;
};

export type AssistantFileIndexingStatus =
  | 'in_progress'
  | 'completed'
  | 'failed';

export type AssistantFile = {
  id?: string;
  filename: string;
  usage_bytes: number;
  createdAt?: number;
  indexing_status?: AssistantFileIndexingStatus;
  last_error?: string | null;
  /** B2Chat keeps the uploaded original (F002); absent on older backends. */
  original_available?: boolean;
};

interface FetchOptions extends RequestInit {
  headers?: Record<string, string>;
  /** `'blob'` returns the body as a Blob (a binary original, ENG-2053). */
  responseType?: 'blob';
}

export class AssistantApiError extends Error {
  status: number;

  body: unknown;

  constructor(status: number, body: unknown) {
    super(`HTTP error! Status: ${status}`);
    this.name = 'AssistantApiError';
    this.status = status;
    this.body = body;
  }
}

export async function fetchAssistantApi<T>(
  url: string,
  options?: FetchOptions,
  isMultipart = false
): Promise<T> {
  const token = await APIClient.getJWT();
  const { responseType, ...requestInit } = options ?? {};

  return fetch(url, {
    ...requestInit,
    headers: {
      ...(!isMultipart ? { 'Content-Type': 'application/json' } : {}),
      Authorization: `Bearer ${token}`,
      ...(options?.headers || {}),
    },
  }).then(async response => {
    if (!response.ok) {
      const body = await response.json().catch(() => null);
      throw new AssistantApiError(response.status, body);
    }
    if (responseType === 'blob') {
      return response.blob() as Promise<T>;
    }
    // Detect the content type
    const contentType = response.headers.get('Content-Type');

    if (contentType?.includes('application/json')) {
      return response.json() as Promise<T>;
    }

    return response.text() as Promise<T>;
  });
}
